import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from "axios";

import { store } from "../redux/store";
import { logout, refreshTokenSuccess } from "../redux/auth/authSlice";
import { API_URL, REQUEST_TIMEOUT, STORAGE_KEYS } from "../constants/api";

// --- Type Definitions ---

interface TokenResponse {
  status: boolean;
  message: string;
  data: {
    accessToken: string;
  };
  errors: null;
}

interface FailedQueueItem {
  resolve: (value: string | null) => void;
  reject: (reason?: unknown) => void;
}

// --- Token Storage Helpers ---

const getToken = async (): Promise<string | null> => {
  try {
    return await localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  } catch (error: unknown) {
    console.error("Failed to get access token from storage", error);
    return null;
  }
};

const setToken = async (accessToken: string): Promise<void> => {
  try {
    await localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
  } catch (error: unknown) {
    console.error("Failed to set access token in storage", error);
    throw error;
  }
};

const clearTokens = async (): Promise<void> => {
  try {
    await localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  } catch (error: unknown) {
    console.error("Failed to clear tokens from storage", error);
    throw error;
  }
};

// --- Base Axios Configuration ---

const baseConfig: AxiosRequestConfig = {
  baseURL: API_URL,
  timeout: REQUEST_TIMEOUT,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  withCredentials: true, // Enable cookies for refresh token
};

// --- Public API Client (No Auth) ---

export const publicApiClient: AxiosInstance = axios.create(baseConfig);

// --- Private API Client (With Auth & Refresh Logic) ---

export const privateApiClient: AxiosInstance = axios.create(baseConfig);

// --- Dynamic Headers Interceptor ---

const addDynamicHeaders = async (
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> => {
  try {
    const language = (await localStorage.getItem("lang")) || "en";
    config.headers["Accept-Language"] = language;
    return config;
  } catch (error: unknown) {
    console.error("Failed to get language for headers", error);
    return config;
  }
};

// Apply to both clients
publicApiClient.interceptors.request.use(
  addDynamicHeaders,
  (error: AxiosError) => Promise.reject(error)
);
privateApiClient.interceptors.request.use(
  addDynamicHeaders,
  (error: AxiosError) => Promise.reject(error)
);

// --- Token Interceptor for Private Client ---

privateApiClient.interceptors.request.use(
  async (
    config: InternalAxiosRequestConfig
  ): Promise<InternalAxiosRequestConfig> => {
    const token = await getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// --- Token Refresh Logic ---

let isRefreshing = false;
let failedQueue: FailedQueueItem[] = [];

const processQueue = (
  error: Error | null,
  token: string | null = null
): void => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

// Response Interceptor for Private Client
privateApiClient.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  async (error: AxiosError): Promise<unknown> => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          if (token && originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }

          return privateApiClient.request(originalRequest);
        })
        .catch((err) => {
          Promise.reject(err);
        });
    }

    isRefreshing = true;

    try {
      // Refresh token is stored in httpOnly cookie, so we send empty body
      const response: AxiosResponse<TokenResponse> = await publicApiClient.post(
        "/auth/refresh-token",
        {}, // Empty body - refresh token comes from httpOnly cookie
        { 
          withCredentials: true, // Ensure cookies are sent and received
        }
      );

      // Check response structure
      if (!response.data || !response.data.data || !response.data.data.accessToken) {
        console.error("Invalid refresh token response structure:", response.data);
        throw new Error("Invalid refresh token response: missing accessToken");
      }

      const { accessToken } = response.data.data;

      // Save new token to localStorage
      await setToken(accessToken);
      
      // Update Redux store
      store.dispatch(refreshTokenSuccess({ accessToken }));

      // Update default headers for future requests
      privateApiClient.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
      
      // Update the original request headers
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      }

      // Process queued requests
      isRefreshing = false;
      processQueue(null, accessToken);

      // Retry the original request with new token
      return privateApiClient.request(originalRequest);
    } catch (refreshError: unknown) {
      console.error("Token refresh failed:", refreshError);
      
      // Clear tokens and logout user
      store.dispatch(logout());
      await clearTokens();

      // Process queued requests with error
      isRefreshing = false;
      processQueue(
        refreshError instanceof Error
          ? refreshError
          : new Error("Token refresh failed")
      );

      return Promise.reject(refreshError);
    }
  }
);

// --- Error Message Helper ---

export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{
      message?: string;
      error?: string;
      errors?: string;
    }>;

    if (axiosError.response?.data) {
      const { message, error, errors } = axiosError.response.data;
      if (message) return message;
      if (error) return error;
      if (errors) return errors;
    }

    if (axiosError.response?.statusText) {
      return axiosError.response.statusText;
    }

    return axiosError.message || "An unexpected error occurred";
  }

  return error instanceof Error
    ? error.message
    : "An unexpected error occurred";
};
